import {
  type Action,
  PharmacyType,
  UserLanguage,
  PlanSnpType,
  type SearchResultsFilters,
  type SnpFilter,
} from "../../@types";

import { saveBenePharmacies } from "../../api";
import { hasGlobalSession } from "../../helpers/loginHelpers";
// ! Do not attempt to import from re-exports (../../helpers) at the moment
// ! This breaks tests
import { cachedStore, storageKey } from "../../helpers/storeHelpers";
import { emptyEnrollData } from "../../helpers/enrollmentFormHelpers";
import { type ApiError, logError } from "../../helpers/errors";

import * as TypeGuards from "../../@types/guards";
import { isDSNP } from "../../helpers/lisHelpers";
import isEqual from "fast-deep-equal";
import { useAppState } from "../../helpers/context-hooks/useAppState";
import type { AppState } from "../../@types/context";
import type { EventTrackingPayload } from "../contexts/Analytics/types";

const navigatorLanguage = navigator.language.split(/[-_]/)[0];
const defaultLanguage = (() => {
  switch (navigatorLanguage) {
    case UserLanguage.English:
    case UserLanguage.Spanish: {
      return navigatorLanguage;
    }
    default: {
      return UserLanguage.English;
    }
  }
})();

export const getDefaultState = (): AppState => {
  const defaultState: AppState = {
    anonProviders: [],
    beneficiary: undefined,
    compareList: [],
    county: undefined,
    csr: undefined,
    csrGuestAccess: false,
    cwCoverage: undefined,
    degradedIntegrations: [],
    demographicInfo: undefined,
    dismissedAlerts: [],
    enrollData: emptyEnrollData,
    fips: undefined,
    futureLis: undefined,
    language: defaultLanguage,
    lis: undefined,
    mapboxPharmacySuggestions: [],
    maxProviderSearchRadius: 50,
    mbpHandoffFailed: false,
    mbpReferrerUri: "",
    anonNewToMedicareAlertInfo: undefined,
    pharmacies: [],
    pharmacySearchAddress: "",
    pharmacySearchSource: null,
    pharmacyType: PharmacyType.None,
    prescriptions: [],
    providerSelectedDuringSession: false,
    routeParams: {},
    searchResultsFilters: {
      provider: "",
      drugCoveragesFilter: [],
      insuranceCarriersFilter: [],
      insuranceCarriers: [],
      planCategoriesFilter: [],
      planCoveragesFilter: [],
      sortBy: undefined,
      starRatingsFilter: [],
      includeSnp: false,
    },
    snpFilters: [],
    tealiumLoaded: false,
    trackedEvents: {},
    zipcode: undefined,
    totalSearchResults: 0,
  };
  return { ...defaultState };
};

export const initialAppState: AppState = cachedStore || getDefaultState();

/**
 * Restores `AppState` to "default" while preserving  `language` from the previous
 * session
 *
 * Deletes `cms-mct-store` (the store) from `sessionStorage`, but because the two
 * are always kept in sync, it will immediately be recreated with what is
 * returned here.
 */
export const resetState = ({
  language,
}: {
  language: UserLanguage;
}): AppState => {
  // Clear session storage relative to domain
  window.sessionStorage.removeItem(storageKey);
  const defaultState = getDefaultState();

  return {
    ...defaultState,
    language,
  };
};

export function isMailOrderPharmacy(pharmacyType: PharmacyType) {
  return [PharmacyType.Mail, PharmacyType.MailAndRetail].includes(pharmacyType);
}
/** @deprecated use `isMailOrderPharmacy` or `useHasMailOrderPharmacy` directly to avoid passing a full state to a function */
export const hasMailOrderPharmacy = (
  state: Partial<AppState> & Pick<AppState, "pharmacyType">
): boolean => isMailOrderPharmacy(state.pharmacyType);

export function useHasMailOrderPharmacy() {
  const { pharmacyType } = useAppState();
  return isMailOrderPharmacy(pharmacyType);
}

export const addPharmacy = (
  state: AppState,
  { payload: pharmacy }: Action
): AppState => {
  if (TypeGuards.isPharmacy(pharmacy)) {
    const isNewPharmacy = !state.pharmacies.some(p => p.npi === pharmacy.npi);
    return isNewPharmacy
      ? { ...state, pharmacies: [...state.pharmacies, pharmacy] }
      : state;
  }

  return state;
};

export const deletePharmacy = (
  state: AppState,
  { payload: npi }: Action
): AppState => {
  if (typeof npi === "string") {
    const existingPharmacyCount = state.pharmacies.length;
    const pharmacies = state.pharmacies.filter(p => p.npi !== npi);
    return pharmacies.length < existingPharmacyCount
      ? {
          ...state,
          pharmacies,
        }
      : state;
  }
  return state;
};

export const savePharmacies = (state: AppState): AppState => {
  if (hasGlobalSession(state)) {
    (async () => {
      const npis = state.pharmacies.map(p => p.npi);
      if (hasMailOrderPharmacy(state)) {
        npis.push("mail-order");
      }
      try {
        await saveBenePharmacies(npis);
      } catch (e) {
        logError(
          "Failed to save beneficiary pharmacies (AppStore/savePharmacies)",
          e as ApiError
        );
      }
    })();
  }
  return state;
};

export const setPharmacies = (
  state: AppState,
  { payload }: Action
): AppState => {
  const pharmacies = TypeGuards.hasPharmacies(payload)
    ? payload
    : state.pharmacies;

  return { ...state, pharmacies };
};

export const setMailOrderPharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.isMailOrderPharmacyStatus(payload)) {
    if (isEqual(state.mailOrderNetworkStatus, payload)) {
      return state;
    }
    return { ...state, mailOrderNetworkStatus: payload };
  }
  return state;
};

export const addProvider = (state: AppState, { payload }: Action): AppState => {
  if (TypeGuards.isProvider(payload)) {
    const provider = payload;
    const isNewProvider = !state.anonProviders.some(
      p => p.npi === provider.npi
    );
    return isNewProvider
      ? { ...state, anonProviders: [...state.anonProviders, provider] }
      : state;
  }

  return state;
};

export const deleteProvider = (
  state: AppState,
  { payload: npi }: Action
): AppState => {
  if (typeof npi === "string") {
    const currentProviderCount = state.anonProviders.length;
    const remainingProviders = state.anonProviders.filter(p => p.npi !== npi);
    return remainingProviders.length < currentProviderCount
      ? {
          ...state,
          anonProviders: remainingProviders,
        }
      : state;
  }
  return state;
};

export const updateLis = (
  state: AppState,
  { payload: lis }: Action,
  setFutureLis = false
): AppState => {
  if (!TypeGuards.isLis(lis)) {
    return state;
  }

  const {
    searchResultsFilters: initialSearchResultsFilters,
    snpFilters: initialSnpFilters,
  } = state;
  const shouldIncludeDsnp = isDSNP(lis);
  const updateSnpFilters =
    shouldIncludeDsnp && !initialSnpFilters.includes(PlanSnpType.DualEligible);

  const includeSnp =
    shouldIncludeDsnp || initialSearchResultsFilters.includeSnp;
  const snpFilters: SnpFilter[] = updateSnpFilters
    ? [...initialSnpFilters, PlanSnpType.DualEligible]
    : initialSnpFilters;

  const searchResultsFilters: SearchResultsFilters = {
    ...initialSearchResultsFilters,
    includeSnp,
  };

  return setFutureLis
    ? { ...state, futureLis: lis, snpFilters, searchResultsFilters }
    : { ...state, lis, snpFilters, searchResultsFilters };
};

export const updatePlanType = (
  state: AppState,
  { payload }: Action
): AppState => {
  const planType = TypeGuards.isPlanType(payload) ? payload : undefined;
  if (state.planType === planType) {
    return state;
  }
  const { searchResultsFilters } = getDefaultState();
  return {
    ...state,
    planType,
    searchResultsFilters: {
      ...searchResultsFilters,
      includeSnp: state.searchResultsFilters.includeSnp,
    },
  };
};

/**
 * Updates the plan-specific network status for a pharmacy. Called whenever
 * `PharmacyNetworkStatus` is used, to prevent flash-of-incorrect-state issues
 * when switching between plans
 */
export const updatePharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.isPharmacyInfo(payload)) {
    const pharmacies = state.pharmacies.map(p =>
      p.npi === payload.npi
        ? { ...p, in_network: payload.in_network, preferred: payload.preferred }
        : p
    );
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

export const updateSearchResultsFiltersCarriers = (
  state: AppState,
  { payload }: Action
): AppState => {
  const insuranceCarriers = TypeGuards.hasInsuranceCarriers(payload)
    ? payload
    : [];

  return {
    ...state,
    searchResultsFilters: {
      ...state.searchResultsFilters,
      insuranceCarriers,
    },
  };
};

export const updateTrackedEvents = (
  state: AppState,
  { payload }: Action
): AppState => {
  const eventTrackingPayload = payload as EventTrackingPayload;
  const eventToRegister = TypeGuards.isRegisteredEvent(
    eventTrackingPayload?.event
  )
    ? eventTrackingPayload.event
    : null;
  if (eventToRegister && eventTrackingPayload?.value !== undefined) {
    return {
      ...state,
      trackedEvents: {
        ...state.trackedEvents,
        [eventToRegister]: eventTrackingPayload.value,
      },
    };
  }
  return state;
};

export const updateSearchResultsFilters = (
  state: AppState,
  { payload }: Action
): AppState => {
  const filters = TypeGuards.hasSearchResultsFilters(payload) ? payload : null;
  const hasProvider = !!(filters && filters.provider);
  return filters
    ? {
        ...state,
        /**
         * The first time a provider is selected, the boolean `providerSelectedDuringSession`
         * field is flipped in `AppState`, and on any subsequent updates, including
         * removing the `provider` filter, this boolean remains set to `true` (for
         * the entire user session)
         */
        providerSelectedDuringSession:
          state.providerSelectedDuringSession || hasProvider,
        searchResultsFilters: filters,
      }
    : state;
};
